import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { MattingOptions } from '@meitu/image-matting';
import type { AI_GENERATE_IMAGE_EXT_DATA } from '@meitu/saas-editor-core/es/version-types';

export interface IAIExpendImageInfo {
    // 扩图倍数
    multiple: number;

    // 扩图比例
    ratio: string;

    // 扩图描述
    desc: string;

    // 结果图
    resultUrl: string;

    loading: boolean;
    offsetX: number;
    offsetY: number;
}

export enum AIModifyImagePainType {
    pain,
    erase,
    none,
}

export interface IAIModifyImageInfo {
    desc: string;
    mask: string;
    painType: AIModifyImagePainType;
    painSize: number;
    loading: boolean;
    hasPain: boolean;

    // 结果图
    resultUrl: string;
}

export const DefaultAIExpendImageInfo = () => ({
    multiple: 1.25,
    ratio: '0',
    desc: '',
    resultUrl: '',
    offsetX: 0,
    offsetY: 0,
    loading: false,
});

export const DefaultAIModifyImageInfo = () => ({
    desc: '',
    mask: '',
    painType: AIModifyImagePainType.pain,
    painSize: 30,
    loading: false,
    resultUrl: '',
    hasPain: false,
});

interface FuncUseMapPrams {
    [key: string]: {
        [key: string]: {
            func: string;
            multiple?: number;
            ratio?: string;
            is_creative?: boolean;
            url?: string;
            size_num?: number;

            [key: string]: any;
        };
    };
}

export interface IPluginModel {
    aiExpendImageInfo: IAIExpendImageInfo;
    setAIExpendImageInfo: (payload: IAIExpendImageInfo) => void;
    aiModifyImageInfo: IAIModifyImageInfo;
    setAIModifyImageInfo: (payload: IAIModifyImageInfo) => void;
    funcUsedMap: FuncUseMapPrams;
    setFuncUsedMap: (payload: FuncUseMapPrams) => void;
    batchTemplateUse: number[];
    setBatchTemplateUse: (payload: number) => void;
    openImageModalType: string | null;
    setOpenImageModalType: (payload: string) => void;
    processingMattingImgInfo: MattingOptions['imageInfo'] | null;
    setProcessingMattingImgInfo: (payload: MattingOptions['imageInfo']) => void;
    clearProcessingMattingImgInfo: () => void;

    isMatting: boolean;
    setIsMatting: (payload: boolean) => void;
    isMattingMainLayer: boolean;
    setIsMattingMainLayer: (payload: boolean) => void;
    aiMattingCanApply: boolean;
    setAiMattingCanApply: (payload: boolean) => void;

    openPlugName: string | null;
    setOpenPlugName: (payload: string | null) => void;
    isAiExpendImage: boolean;

    setIsAiExpendImage: (payload: boolean) => void;

    isAiModifyImage: boolean;

    setIsAiModifyImage: (payload: boolean) => void;

    isAiGenerateImage: boolean;

    setIsAiGenerateImage: (payload: boolean) => void;

    /** ai生图插件的初始化配置 */
    extDataOfAiGenerateImage: AI_GENERATE_IMAGE_EXT_DATA | null;

    setExtDataOfAiGenerateImage: (payload: AI_GENERATE_IMAGE_EXT_DATA | null) => void;
}

const usePluginModel = create<IPluginModel>()(
    immer((set) => ({
        aiExpendImageInfo: DefaultAIExpendImageInfo(),
        setAIExpendImageInfo: (payload) =>
            set((state) => {
                state.aiExpendImageInfo = payload;
            }),
        aiModifyImageInfo: DefaultAIModifyImageInfo(),
        setAIModifyImageInfo: (payload) =>
            set((state) => {
                state.aiModifyImageInfo = payload;
            }),
        funcUsedMap: {},
        setFuncUsedMap: (payload) =>
            set((state) => {
                state.funcUsedMap = payload;
            }),
        batchTemplateUse: [],
        setBatchTemplateUse: (payload) =>
            set((state) => {
                state.batchTemplateUse.push(payload);
                state.batchTemplateUse = [...state.batchTemplateUse];
            }),
        openImageModalType: '',
        setOpenImageModalType: (payload) =>
            set((state) => {
                state.openImageModalType = payload;
            }),
        isMatting: false,
        setIsMatting: (payload) =>
            set((state) => {
                state.isMatting = payload;
            }),
        isMattingMainLayer: false,
        setIsMattingMainLayer: (payload) =>
            set((state) => {
                state.isMattingMainLayer = payload;
            }),
        aiMattingCanApply: false,
        setAiMattingCanApply: (payload) =>
            set((state) => {
                state.aiMattingCanApply = payload;
            }),
        processingMattingImgInfo: null,
        setProcessingMattingImgInfo: (payload) =>
            set((state) => {
                state.processingMattingImgInfo = payload;
            }),
        clearProcessingMattingImgInfo: () =>
            set((state) => {
                state.processingMattingImgInfo = null;
            }),
        openPlugName: null,
        setOpenPlugName: (payload) =>
            set((state) => {
                state.openPlugName = payload;
            }),
        isAiExpendImage: false,
        isAiModifyImage: false,
        isAiGenerateImage: false,
        extDataOfAiGenerateImage: null,

        setIsAiExpendImage: (payload) =>
            set((state) => {
                state.isAiExpendImage = payload;
            }),

        setIsAiModifyImage: (payload) =>
            set((state) => {
                state.isAiModifyImage = payload;
            }),

        setIsAiGenerateImage: (payload) =>
            set((state) => {
                state.isAiGenerateImage = payload;
            }),

        setExtDataOfAiGenerateImage: (payload) =>
            set((state) => {
                state.extDataOfAiGenerateImage = payload;
            }),
    })),
);

export { usePluginModel };
export const getPluginModel = () => usePluginModel.getState();
